/* eslint-disable no-undef */
import React, { useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';

import {
  APP_ROUTES,
  COLORS,
  ERROR_LOADING_DISPATCHES,
  ids,
  ORIGINS,
  dispatchTypes
} from 'apps/web_forms/utils/constants';
import DispatchedFormIcon from 'apps/web_forms/assets/dispatched-form.svg';
import GeoLocatedFormIcon from 'apps/web_forms/assets/dispatched-form-geolocated.svg';
import GeoFencedFormIcon from 'apps/web_forms/assets/dispatched-form-geofenced.svg';
import MapIcon from 'apps/web_forms/assets/map.svg';
import FormList from 'apps/web_forms/components/FormList';
import FormListElement from 'apps/web_forms/components/FormListElement';
import FormPageHeader from 'apps/web_forms/components/FormPageHeader';
import DMButton from 'apps/web_forms/components/commons/DMButton';
import PlaceHolder from 'apps/web_forms/components/commons/PlaceHolder';
import { locationParse, buildDistanceObject } from 'apps/web_forms/utils/locationHelpers';
import useLocation from 'apps/web_forms/hooks/useLocation';
import { useDispatches } from 'apps/web_forms/hooks/useDispatches';
import { getDirectionUrl } from 'apps/web_forms/utils/googleMapsURLScheme';
import { getMeasureUnitFromString } from 'apps/web_forms/utils/inputsUtils';
import { setSelectedForm } from 'apps/web_forms/slices/appSlice';
import { useDispatch } from 'react-redux';

const dispatchesDescription = `Dispatched forms are sent specifically to you from your organization's administrators.`;

const Dispatches = () => {
  const {
    isLoading,
    isError,
    error,
    regularDispatches,
    geolocatedDispatches,
    geofencedDispatches,
  } = useDispatches();
  const history = useHistory();
  const { setUserAccuracy, center, locationError } = useLocation();

  const dispatch = useDispatch();

  if (isError) {
    // TODO: Integrate Bugsnag to log the errors in prod.
    // eslint-disable-next-line no-console
    console.error(`${ERROR_LOADING_DISPATCHES}:`, error);
  }

  const DispatchesError = () => {
    return (
      <>
        <FormPageHeader
          title={'Dispatches'}
          description={dispatchesDescription}
          small={false}
          renderToggleButton={false}
        />
        <PlaceHolder error>{ERROR_LOADING_DISPATCHES}</PlaceHolder>
      </>
    );
  };

  useEffect(() => {
    setUserAccuracy();
  }, []);

  const goToForm = (id, version, dispatchType) => {
    let selectedDispatch = {};
    switch(dispatchType) {
      case dispatchTypes.GEO_LOCATED:
        selectedDispatch = geolocatedDispatches.find((form) => parseInt(form.identifier) === parseInt(id));
        break;
      case dispatchTypes.GEO_FENCED:
        selectedDispatch = geofencedDispatches.find((form) => parseInt(form.identifier) === parseInt(id));
        break;
      default:
        selectedDispatch = regularDispatches.find((form) => parseInt(form.identifier) === parseInt(id));
    }
    dispatch(setSelectedForm(selectedDispatch));
    history.push(APP_ROUTES.getFormPageById(id, `?origin=${ORIGINS.Dispatch}`), {version,});
  };

  const render = () => {
    return (
      <>
        {/* Dispatches */}
        <FormList
          header
          id={ids.DispatchesIds.MainSection}
          title='Dispatches'
          description={dispatchesDescription}
          loading={isLoading}
          forms={regularDispatches}
        >
          {(form, index) => (
            <FormListElement
              index={index}
              identifier={form.identifier}
              key={form.identifier}
              title={form.title}
              description={form.description}
              iconComponent={DispatchedFormIcon}
              onClick={(formId) => goToForm(formId, form.version, dispatchTypes.REGULAR)}
              buttons={
                form.description ? [
                  {
                    id: ids.DispatchesIds.OpenDispatchButton,
                    label: 'Open',
                    onClick: (formId) => goToForm(formId, form.version, dispatchTypes.REGULAR),
                    highlighted: true,
                  },
                ] : []
              }
              moreOption
            />
          )}
        </FormList>

        {/* Geo-Located Dispatches */}
        <FormList
          id={ids.DispatchesIds.GeolocatedSection}
          title='Geo-Located Dispatches'
          description='Geo-located dispatches are sent specifically to you with a location
          attached.'
          renderRightButton={() => (
            <Link to={APP_ROUTES.getDispatchesDetails('geolocated')}>
              <DMButton
                id={ids.DispatchesIds.GeolocatedDetailsList}
                disabled={isLoading}
                filled
                iconComponent={MapIcon}
              >
                Geo-Located Dispatches Map
              </DMButton>
            </Link>
          )}
          forms={geolocatedDispatches}
          loading={isLoading}
        >
          {(form, index) => {
            const { latitude, longitude } = locationParse(form.location);
            return (
              <FormListElement
                index={index}
                identifier={form.identifier}
                key={form.identifier}
                title={form.title}
                description={form.description}
                iconComponent={GeoLocatedFormIcon}
                onClick={(formId) => goToForm(formId, form.version, dispatchTypes.GEO_LOCATED)}
                buttons={[
                  {
                    id: ids.DispatchesIds.OpenGeolocatedDispatchButton,
                    label: 'Open',
                    onClick: (formId) => goToForm(formId, form.version, dispatchTypes.GEO_LOCATED),
                    highlighted: true,
                  },
                  {
                    id: ids.DispatchesIds.GeolocatedDirectionsButton,
                    label: 'Directions',
                    onClick: center
                      ? () => {
                          window.open(
                            getDirectionUrl(
                              { lat: center.lat, lng: center.lng },
                              { lat: latitude, lng: longitude }
                            ),
                            '_blank'
                          );
                        }
                      : () => {},
                  },
                ]}
                geoInfo={{
                  distance: buildDistanceObject(
                    { latitude, longitude },
                    center,
                    locationError
                  ),
                  location: form.address,
                }}
                moreOption
              />
            );
          }}
        </FormList>

        {/* Geo-Fenced Dispatches */}
        <FormList
          id={ids.DispatchesIds.GeofencedSection}
          title='Geo-Fenced Dispatches'
          description='Geo-fenced dispatches are sent specifically to you with a location and
          must be completed within the determined radius.'
          renderRightButton={() => (
            <Link to={APP_ROUTES.getDispatchesDetails('geofenced')}>
              <DMButton
                id={ids.DispatchesIds.GeofencedDetailsList}
                disabled={isLoading}
                filled
                iconComponent={MapIcon}
              >
                Geo-Fenced Dispatches Map
              </DMButton>
            </Link>
          )}
          loading={isLoading}
          forms={geofencedDispatches}
        >
          {(form, index) => {
            const { latitude, longitude } = locationParse(form.location);
            const distance = buildDistanceObject(
              { latitude, longitude },
              center,
              locationError,
              getMeasureUnitFromString(form?.geoFenceRadius),
              true
            );
            return (
              <FormListElement
                index={index}
                identifier={form.identifier}
                key={form.identifier}
                title={form.title}
                description={form.description}
                iconComponent={GeoFencedFormIcon}
                iconColor={
                  distance.outOfRange
                    ? COLORS.dispatchDisabledColor
                    : COLORS.primary
                }
                iconHeight='1.5em'
                iconLabel={form?.geoFenceRadius}
                onClick={(formId) =>
                  distance.outOfRange || goToForm(formId, form.version, dispatchTypes.GEO_FENCED)
                }
                buttons={[
                  {
                    id: ids.DispatchesIds.OpenGeofencedDispatchButton,
                    label: 'Open',
                    onClick: (formId) => goToForm(formId, form.version, dispatchTypes.GEO_FENCED),
                    highlighted: true,
                    disabled: distance.outOfRange,
                  },
                  {
                    id: ids.DispatchesIds.GeofencedDirectionsButton,
                    label: 'Directions',
                    onClick: center
                      ? () => {
                          window.open(
                            getDirectionUrl(
                              { lat: center.lat, lng: center.lng },
                              { lat: latitude, lng: longitude }
                            ),
                            '_blank'
                          );
                        }
                      : () => {},
                  },
                ]}
                geoInfo={{
                  distance,
                  location: form.address,
                }}
                moreOption
              />
            );
          }}
        </FormList>
      </>
    );
  };

  return isError ? <DispatchesError /> : render();
};

export default Dispatches;
